import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  OutsourcingContractLegRunPdfPreviewRequestEntity,
  OutsourcingContractLegRunPdfRequestEntity,
  OutsourcingContractLegRunSummary,
} from 'src/entities/OutsourcingContractLegRunPdf.entity';
import requestUtil from 'src/utils/request.util';

const requestPath = '/api/v5/shipment_orders/outsourcing_contract_pdf';
const legRunsRequestPath = '/api/v5/shipment_orders/outsourcing_contract_leg_runs';

export const outsourcingContractLegRunsQueryKey = 'useOutsourcingContractLegRunsQuery';

export const useMutationOutsourcingContractLegRunPdf = () => ({
  generate: useMutation({
    mutationFn: (entity: OutsourcingContractLegRunPdfRequestEntity) => {
      const settingsArray = Object.entries(entity.outsourcingContractSettings).map(([legRunId, settings]) => ({
        ...settings,
        legRunId: Number(legRunId),
      }));
      const modifiedEntity = {
        ...entity,
        outsourcingContractSettings: settingsArray,
      };
      // responseType: 'arraybuffer' のため data は ArrayBuffer。
      return axios.post<ArrayBuffer>(
        requestPath,
        requestUtil.convertKeysToSnakeCase(modifiedEntity),
        {
          responseType: 'arraybuffer',
        },
      );
    },
  }),
  create: useMutation({
    mutationFn: (entity: OutsourcingContractLegRunPdfPreviewRequestEntity) => axios.post(
      requestPath,
      requestUtil.convertKeysToSnakeCase(entity),
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    ),
  }),
});

// batch API のレスポンス要素（snake_case）
type OutsourcingContractLegRunResponseItem = {
  leg_run_id: number;
  code: string;
  sequence_number: number;
  loading_name: string | null;
  unloading_name: string | null;
  carrier_company: string | null;
};

type OutsourcingContractLegRunsResponse = {
  data: OutsourcingContractLegRunResponseItem[];
};

const fetchOutsourcingContractLegRuns = async ({
  queryKey,
}: {
  queryKey: readonly [string, { shipmentOrderIds: number[] }];
}): Promise<OutsourcingContractLegRunSummary[]> => {
  const [, { shipmentOrderIds }] = queryKey;
  // 全件選択時に ID が多数になり GET の URL 長制限に達するため、POST body で渡す。
  const response = await axios.post<OutsourcingContractLegRunsResponse>(
    legRunsRequestPath,
    { shipment_order_ids: shipmentOrderIds },
  );
  return (response.data?.data ?? []).map((item) => ({
    legRunId: item.leg_run_id,
    code: item.code,
    sequenceNumber: item.sequence_number,
    loadingName: item.loading_name,
    unloadingName: item.unloading_name,
    carrierCompany: item.carrier_company,
  }));
};

export const useOutsourcingContractLegRunsQuery = (shipmentOrderIds: number[], enabled: boolean) => useQuery({
  queryKey: [outsourcingContractLegRunsQueryKey, { shipmentOrderIds }] as const,
  queryFn: fetchOutsourcingContractLegRuns,
  // 配車変更で leg_run は変化するため無期限キャッシュしない（開くたびに最新取得）。
  staleTime: 0,
  // ダイアログ表示中のフォーカス復帰で再取得されると入力済み設定が初期化されるため抑止する。
  refetchOnWindowFocus: false,
  enabled: enabled && shipmentOrderIds.length > 0,
});
